import { t } from "ttag";
import _ from "underscore";

import { getNullDisplayValue } from "metabase/utils/constants";
import {
  currency,
  getCurrency,
  getCurrencyNarrowSymbol,
  getCurrencyStyleOptions,
  getCurrencySymbol,
  hasHour,
  numberFormatterForOptions,
} from "metabase/utils/formatting";
import MetabaseSettings from "metabase/utils/settings";
import {
  displayNameForColumn,
  getDateFormatFromStyle,
  getDateStyleOptionsForUnit,
  getTimeStyleOptions,
} from "metabase/value-formatting";
import {
  getColumnKey,
  getObjectColumnSettings,
} from "metabase-lib/v1/queries/utils/column-key";
import {
  findColumnIndexesForColumnSettings,
  findColumnSettingIndexesForColumns,
} from "metabase-lib/v1/queries/utils/dataset";
import {
  isCoordinate,
  isCurrency,
  isDate,
  isDateWithoutTime,
  isNumber,
} from "metabase-lib/v1/types/utils/isa";
import type {
  ColumnSettings,
  DatasetColumn,
  DatetimeUnit,
  Series,
  TimeEnabled,
  VisualizationSettings,
} from "metabase-types/api";

import {
  getDefaultCurrency,
  getDefaultCurrencyInHeader,
  getDefaultCurrencyStyle,
  getDefaultNumberSeparators,
  getDefaultNumberStyle,
} from "../../shared/settings/column";
import type {
  ComputedVisualizationSettings,
  FormattableColumn,
  VisualizationSettingsDefinitions,
} from "../../types";
import { getVisualization, getVisualizationRaw } from "../registry";

import { nestedSettings } from "./nested";
import {
  getDeduplicatedTableColumnSettings,
  insertNewColumnSettings,
} from "./utils";

type GetColumnsFn = (
  series: Series,
  vizSettings: VisualizationSettings,
) => DatasetColumn[];

const DEFAULT_GET_COLUMNS: GetColumnsFn = (series, _vizSettings) =>
  series.flatMap((s) => s.data?.cols ?? []);

export interface ColumnSettingsOptions {
  getColumns?: GetColumnsFn;
  getHidden?: (series: Series, settings: VisualizationSettings) => boolean;
  getSection?: () => string;
  readDependencies?: string[];
}

export function columnSettings({
  getColumns = DEFAULT_GET_COLUMNS,
  ...def
}: ColumnSettingsOptions = {}) {
  return nestedSettings<"column_settings", DatasetColumn>("column_settings", {
    getSection: () => t`Formatting`,
    objectName: "column",
    getObjects: getColumns,
    getObjectKey: getColumnKey,
    getObjectSettings: getObjectColumnSettings,
    getSettingDefinitionsForObject: getSettingDefinitionsForColumn,
    widget: "nestedColumns",
    getInheritedSettingsForObject: getInheritedSettingsForColumn,
    useRawSeries: true,
    ...def,
  });
}

export function getGlobalSettingsForColumn(): ColumnSettings {
  const columnSettings: ColumnSettings = {};
  const customFormatting = MetabaseSettings.get("custom-formatting") || {};

  // NOTE: the order of these doesn't matter as long as there's no overlap between settings
  for (const [, globalSettings] of Object.entries(customFormatting)) {
    Object.assign(columnSettings, globalSettings);
  }

  return columnSettings;
}

function getLocalSettingsForColumn(column: DatasetColumn): ColumnSettings {
  return column.settings || {};
}

function getInheritedSettingsForColumn(column: DatasetColumn): ColumnSettings {
  return {
    ...getGlobalSettingsForColumn(),
    ...getLocalSettingsForColumn(column),
  };
}

function getTimeEnabledOptionsForUnit(
  unit: DatetimeUnit | "second" | "millisecond" | undefined,
): { name: string; value: TimeEnabled | null }[] {
  const options: { name: string; value: TimeEnabled | null }[] = [
    { name: t`Off`, value: null },
    { name: t`HH:MM`, value: "minutes" },
  ];

  if (
    !unit ||
    unit === "default" ||
    unit === "second" ||
    unit === "millisecond"
  ) {
    options.push({ name: t`HH:MM:SS`, value: "seconds" });
  }

  if (!unit || unit === "default" || unit === "millisecond") {
    options.push({ name: t`HH:MM:SS.MS`, value: "milliseconds" });
  }

  if (options.length === 2) {
    options[1].name = t`On`;
  }

  return options;
}

const hasCoarseDateGranularity = (settings: ColumnSettings) =>
  settings.date_granularity != null && settings.date_granularity !== "default";

export const DATE_COLUMN_SETTINGS: VisualizationSettingsDefinitions = {
  date_style: {
    get title() {
      return t`Date style`;
    },
    widget: "select",
    getDefault: ({ unit }) => {
      // Grab the first option's value. If there were no options (for
      // hour-of-day probably), use an empty format string instead.
      const [{ value = "" } = {}] = getDateStyleOptionsForUnit(unit);
      return value;
    },
    isValid: ({ unit }, settings) => {
      const options = getDateStyleOptionsForUnit(unit ?? "default");
      return !!_.findWhere(options, {
        value: settings.date_style ?? undefined,
      });
    },
    getProps: ({ unit }, settings) => ({
      options: getDateStyleOptionsForUnit(
        unit ?? "default",
        settings.date_abbreviate != null
          ? Boolean(settings.date_abbreviate)
          : undefined,
        settings.date_separator != null
          ? String(settings.date_separator)
          : undefined,
      ),
    }),
    getHidden: ({ unit }, settings) =>
      hasCoarseDateGranularity(settings) ||
      getDateStyleOptionsForUnit(unit ?? "default").length < 2,
    readDependencies: ["date_granularity"],
  },
  date_separator: {
    get title() {
      return t`Date separators`;
    },
    widget: "radio",
    getDefault: () => "/",
    getProps: (_column, settings) => {
      const style = /\//.test(settings.date_style ?? "")
        ? (settings.date_style ?? "")
        : "M/D/YYYY";
      return {
        options: [
          { name: style, value: "/" },
          { name: style.replace(/\//g, "-"), value: "-" },
          { name: style.replace(/\//g, "."), value: "." },
        ],
      };
    },
    getHidden: (_column, settings) =>
      hasCoarseDateGranularity(settings) ||
      !/\//.test(String(settings.date_style ?? "")),
    readDependencies: ["date_style", "date_granularity"],
  },
  date_abbreviate: {
    get title() {
      return t`Abbreviate days and months`;
    },
    widget: "toggle",
    getDefault: () => false,
    inline: true,
    getHidden: ({ unit }, settings) => {
      if (hasCoarseDateGranularity(settings)) {
        return true;
      }
      const format = getDateFormatFromStyle(
        settings.date_style ?? undefined,
        unit ?? "default",
      );
      return !format || !format.match(/MMMM|dddd/);
    },
    readDependencies: ["date_style", "date_granularity"],
  },
  time_enabled: {
    get title() {
      return t`Show the time`;
    },
    widget: "radio",
    isValid: ({ unit }, settings) => {
      const options = getTimeEnabledOptionsForUnit(unit);
      return !!_.findWhere(options, { value: settings.time_enabled });
    },
    getProps: ({ unit }) => {
      const options = getTimeEnabledOptionsForUnit(unit);
      return { options };
    },
    getHidden: (column, settings) =>
      hasCoarseDateGranularity(settings) ||
      !hasHour(column.unit) ||
      isDateWithoutTime(column),
    getDefault: ({ unit }) => (hasHour(unit) ? "minutes" : null),
    readDependencies: ["date_granularity"],
  },
  time_style: {
    get title() {
      return t`Time style`;
    },
    widget: "radio",
    getDefault: () => "h:mm A",
    getProps: (column) => ({
      options: getTimeStyleOptions(column.unit ?? "default"),
    }),
    getHidden: (column, settings) =>
      hasCoarseDateGranularity(settings) ||
      !settings.time_enabled ||
      isDateWithoutTime(column),
    readDependencies: ["time_enabled", "date_granularity"],
  },
};

export const NUMBER_COLUMN_SETTINGS: VisualizationSettingsDefinitions = {
  number_style: {
    get title() {
      return t`Style`;
    },
    widget: "select",
    getProps: () => ({
      options: [
        {
          get name() {
            return t`Normal`;
          },
          value: "decimal",
        },
        {
          get name() {
            return t`Percent`;
          },
          value: "percent",
        },
        {
          get name() {
            return t`Scientific`;
          },
          value: "scientific",
        },
        {
          get name() {
            return t`Currency`;
          },
          value: "currency",
        },
      ],
    }),
    getDefault: getDefaultNumberStyle,
    // hide this for currency
    getHidden: (column, settings) =>
      isCurrency(column) && settings.number_style === "currency",
    readDependencies: ["currency"],
  },
  currency: {
    get title() {
      return t`Unit of currency`;
    },
    widget: "select",
    getProps: () => ({
      // FIXME: rest of these options
      options: currency.map(([_code, c]) => ({
        name: c.name,
        value: c.code,
      })),
      searchProp: "name",
      searchCaseSensitive: false,
    }),
    getDefault: getDefaultCurrency,
    getHidden: (_column, settings) => settings.number_style !== "currency",
  },
  currency_style: {
    get title() {
      return t`Currency label style`;
    },
    widget: "radio",
    getProps: (_column, settings) => ({
      options: getCurrencyStyleOptions(
        settings.currency || "USD",
        settings.currency_style,
      ),
    }),
    getDefault: getDefaultCurrencyStyle,
    getHidden: (_column, settings) => settings.number_style !== "currency",
    readDependencies: ["number_style"],
  },
  currency_in_header: {
    get title() {
      return t`Where to display the unit of currency`;
    },
    widget: "radio",
    getProps: (_series, _vizSettings, onChange) => ({
      onChange: (value: unknown) => onChange(value === true),
      options: [
        { name: t`In the column heading`, value: true },
        { name: t`In every table cell`, value: false },
      ],
    }),
    getDefault: getDefaultCurrencyInHeader,
    getHidden: (_column, settings, extra) => {
      const { forAdminSettings, series = [] } = extra ?? {};
      if (forAdminSettings === true) {
        return false;
      }
      return (
        settings.number_style !== "currency" ||
        series[0]?.card.display !== "table"
      );
    },
    readDependencies: ["number_style"],
  },
  number_separators: {
    // uses 1-2 character string to represent decimal and thousands separators
    get title() {
      return t`Separator style`;
    },
    widget: "select",
    getProps: () => ({
      options: [
        { name: "100,000.00", value: ".," },
        { name: "100 000,00", value: ", " },
        { name: "100.000,00", value: ",." },
        { name: "100000.00", value: "." },
        { name: "100’000.00", value: ".’" },
      ],
    }),
    getDefault: getDefaultNumberSeparators,
  },
  decimals: {
    get title() {
      return t`Number of decimal places`;
    },
    widget: "number",
    getProps: () => ({
      placeholder: "1",
      options: {
        isNonNegative: true,
        isInteger: true,
      },
    }),
  },
  scale: {
    get title() {
      return t`Multiply by a number`;
    },
    widget: "number",
    getProps: () => ({
      placeholder: "1",
    }),
  },
  prefix: {
    get title() {
      return t`Add a prefix`;
    },
    widget: "input",
    getProps: () => ({
      placeholder: "$",
    }),
  },
  suffix: {
    get title() {
      return t`Add a suffix`;
    },
    widget: "input",
    getProps: () => ({
      get placeholder() {
        return t`dollars`;
      },
    }),
  },
  // Optimization: build a single NumberFormat object that is used by formatting.js
  _numberFormatter: {
    getValue: (_column, settings) => numberFormatterForOptions(settings),
    readDependencies: [
      "number_style",
      "currency_style",
      "currency",
      "decimals",
    ],
  },
  _header_unit: {
    getValue: (_column, settings) => {
      if (settings.number_style === "currency" && settings.currency_in_header) {
        if (settings.currency_style === "symbol") {
          return getCurrencySymbol(settings.currency);
        }
        if (settings.currency_style === "narrowSymbol") {
          return getCurrencyNarrowSymbol(settings.currency);
        }

        return getCurrency(settings.currency, settings.currency_style);
      }
      return null;
    },
    readDependencies: [
      "number_style",
      "currency",
      "currency_style",
      "currency_header_only",
    ],
  },
};

const COMMON_COLUMN_SETTINGS: VisualizationSettingsDefinitions = {
  // markdown_template: {
  //   title: t`Markdown template`,
  //   widget: "input",
  //   getProps: () => ({
  //     placeholder: "{{value}}",
  //   }),
  // },
  column: {
    getValue: (column) => column,
  },
  _column_title_full: {
    getValue: (column, settings) => {
      let columnTitle =
        settings["column_title"] || displayNameForColumn(column);
      const headerUnit = settings["_header_unit"];
      if (headerUnit) {
        columnTitle += ` (${headerUnit})`;
      }
      return columnTitle;
    },
    readDependencies: ["column_title", "_header_unit"],
  },
};

export function getSettingDefinitionsForColumn(
  series: Series,
  column: FormattableColumn,
) {
  // ColumnSettings passes an empty fake series when formatting outside a viz;
  // fall back to the default visualization's column settings in that case
  const visualization =
    series.length > 0 ? getVisualizationRaw(series) : getVisualization(null);
  const extraColumnSettings =
    typeof visualization?.columnSettings === "function"
      ? visualization.columnSettings(column)
      : visualization?.columnSettings || {};

  if (isDate(column) || (column.unit && column.unit !== "default")) {
    return {
      ...extraColumnSettings,
      ...DATE_COLUMN_SETTINGS,
      ...COMMON_COLUMN_SETTINGS,
    };
  }
  if (isNumber(column) && !isCoordinate(column)) {
    return {
      ...extraColumnSettings,
      ...NUMBER_COLUMN_SETTINGS,
      ...COMMON_COLUMN_SETTINGS,
    };
  }
  return {
    ...extraColumnSettings,
    ...COMMON_COLUMN_SETTINGS,
  };
}

export function isPivoted(
  series: Series,
  settings: ComputedVisualizationSettings,
): boolean {
  const [{ data }] = series;

  if (!settings["table.pivot"]) {
    return false;
  }

  const pivotIndex = _.findIndex(
    data.cols,
    (col) => col.name === settings["table.pivot_column"],
  );
  const cellIndex = _.findIndex(
    data.cols,
    (col) => col.name === settings["table.cell_column"],
  );
  const normalIndex = _.findIndex(
    data.cols,
    (_col, index) => index !== pivotIndex && index !== cellIndex,
  );

  return pivotIndex >= 0 && cellIndex >= 0 && normalIndex >= 0;
}

export const getTitleForColumn = (
  column: DatasetColumn,
  series: Series,
  settings: ComputedVisualizationSettings,
): string => {
  const pivoted = isPivoted(series, settings);

  if (pivoted) {
    return displayNameForColumn(column) || getNullDisplayValue();
  }

  return (
    settings?.column?.(column)._column_title_full ||
    displayNameForColumn(column)
  );
};

export function tableColumnSettings({
  isShowingDetailsOnlyColumns = false,
}: {
  isShowingDetailsOnlyColumns?: boolean;
} = {}): VisualizationSettingsDefinitions {
  return {
    "table.columns": {
      getSection: () => t`Columns`,
      // title: t`Columns`,
      widget: "tableColumns",
      getHidden: (_series, vizSettings) => vizSettings["table.pivot"] ?? false,
      getValue: ([{ data }], vizSettings) => {
        const { cols } = data;
        const settings = vizSettings["table.columns"] ?? [];
        const uniqColumnSettings = getDeduplicatedTableColumnSettings(settings);

        const columnIndexes = findColumnIndexesForColumnSettings(
          cols,
          uniqColumnSettings,
        );
        const settingIndexes = findColumnSettingIndexesForColumns(
          cols,
          uniqColumnSettings,
        );

        return insertNewColumnSettings(
          // retain settings with matching columns only
          uniqColumnSettings.filter(
            (_, settingIndex) => columnIndexes[settingIndex] >= 0,
          ),
          cols,
          {
            getColumnName: (setting) => setting.name,
            isNewColumn: (_, columnIndex) => settingIndexes[columnIndex] < 0,
            createSetting: (column) => ({ name: column.name, enabled: true }),
          },
        );
      },
      getProps: (series, settings) => {
        const [
          {
            data: { cols },
          },
        ] = series;

        return {
          columns: cols,
          isShowingDetailsOnlyColumns,
          getColumnName: (column) =>
            getTitleForColumn(column, series, settings),
        };
      },
    },
  };
}
